/**
 * Création d'un objet porté par le compagnon, dessin compris.
 *
 * Avant cet écran, ajouter un objet demandait une migration SQL, quatre fichiers
 * préparés à la main et 36 placements dans `tools/editeur-habillage` (48 pour un
 * objet tenu, une boîte par main). Ici : un formulaire, un dessin, et — si
 * l'identifiant dit que c'est une variante (`_2`, `_3`…) — rien à placer du tout,
 * l'objet reprend la boîte de sa base.
 *
 * Avec `objet`, la même fenêtre REPREND un objet existant : son dessin et sa découpe
 * actuels sont chargés comme point de départ — on retouche la peinture de l'arrière,
 * on change de cadre ou de fichier, et le serveur refait les moitiés et l'icône.
 *
 * 🔴 Les deux moitiés d'un objet qui ENTOURE le compagnon (écharpe, cape, collier,
 * lunettes) doivent vivre dans le même cadre que le dessin. On ne demande donc pas
 * deux fichiers : on peint la zone qui passe derrière, et le serveur découpe.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Typography,
} from '@mui/material';
import { AdminService } from '../../services/adminService';
// 🔴 `API_URL` et pas `getAssetUrl` : celui-ci préfixe `/assets/`, et le serveur
// rend déjà un chemin complet (`/assets/cadres/...`) — on obtenait `/assets/assets/`.
import { API_URL } from '../../utils/axios-config';

/** Les trois emplacements du compagnon, avec le préfixe d'identifiant d'usage. */
const TYPES = [
  { type: 'companion_head', libelle: 'Coiffe (tête)', prefixe: 'ch', subType: 'head' },
  { type: 'companion_accessory', libelle: 'Accessoire (corps)', prefixe: 'ca', subType: 'accessory' },
  { type: 'companion_hand', libelle: 'Objet tenu (main)', prefixe: 'cmh', subType: 'hand_item' },
];
const RARETES = ['common', 'rare', 'epic', 'legendary'];
/** Même règle que `backend/services/habillageVariantes.js` : les familles sont plates. */
const SUFFIXE_VARIANTE = /^([a-z0-9_]*[a-z])_\d+$/;

const sansAccent = (t) => t.normalize('NFD').replace(/[̀-ͯ]/g, '');

/** `Lunettes Rondes Dorées` + accessoire → `ca_lunettes_rondes_dorees`. */
function identifiantPropose(nom, type) {
  const prefixe = TYPES.find((t) => t.type === type)?.prefixe;
  const corps = sansAccent(String(nom || '').toLowerCase())
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return prefixe && corps ? `${prefixe}_${corps}` : corps;
}

const formulaireVide = () => ({
  name: '',
  id: '',
  idTouche: false,
  type: 'companion_accessory',
  rarity: 'common',
  price: 150,
  levelRequired: 1,
  description: '',
  icon: '',
});

export default function NouvelObjetPorteDialog({ ouvert, onFermer, onCree, objet = null, onEnregistre }) {
  const [form, setForm] = useState(formulaireVide());
  const [fichier, setFichier] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [pinceau, setPinceau] = useState(28);
  const [outil, setOutil] = useState('peindre');
  const [apercu, setApercu] = useState('peinture');
  const [aPeint, setAPeint] = useState(false);
  const [cadres, setCadres] = useState([]);
  const [cadre, setCadre] = useState(null);

  // Objet existant : son dessin entier et sa moitié arrière, tels que l'app les dessine.
  const urlExistante = objet ? (objet.imageUrl || objet.inventoryImageUrl || null) : null;
  const arriereExistant = objet ? (objet.habillage?.arriere || objet.imageBackUrl || null) : null;
  const aUnDessin = !!fichier || !!urlExistante;
  // Le canvas n'existe qu'une fois la fenêtre montée : on attend qu'il soit là.
  const [vueEl, setVueEl] = useState(null);

  const imageRef = useRef(null);
  const masqueRef = useRef(null);   // canvas hors écran, à la taille réelle du dessin
  const vueRef = useRef(null);      // canvas affiché
  const dessineRef = useRef(false);

  const champ = (cle, valeur) => setForm((f) => ({ ...f, [cle]: valeur }));

  // L'identifiant se propose tout seul tant qu'on n'y a pas touché.
  useEffect(() => {
    setForm((f) => (f.idTouche ? f : { ...f, id: identifiantPropose(f.name, f.type) }));
  }, [form.name, form.type, form.idTouche]);

  const base = SUFFIXE_VARIANTE.exec(form.id || '')?.[1] || null;

  // Cadre de l'icône d'inventaire : les décors du village portent le même.
  useEffect(() => {
    if (!ouvert) return;
    AdminService.getCatalogIconFrames()
      .then((d) => {
        setCadres(d?.cadres || []);
        setCadre((c) => (c === null ? (d?.defaut ?? null) : c));
      })
      .catch(() => setCadres([]));
  }, [ouvert]);

  const redessiner = useCallback(() => {
    const vue = vueRef.current;
    const image = imageRef.current;
    const masque = masqueRef.current;
    if (!vue || !image || !masque) return;
    const ctx = vue.getContext('2d');
    ctx.clearRect(0, 0, vue.width, vue.height);
    ctx.drawImage(image, 0, 0, vue.width, vue.height);
    if (apercu === 'avant' || apercu === 'arriere') {
      // La moitié montrée : on retire l'autre, exactement comme le fera le serveur.
      ctx.globalCompositeOperation = apercu === 'arriere' ? 'destination-in' : 'destination-out';
      ctx.drawImage(masque, 0, 0, vue.width, vue.height);
      ctx.globalCompositeOperation = 'source-over';
      return;
    }
    ctx.globalAlpha = 0.45;
    ctx.drawImage(masque, 0, 0, vue.width, vue.height);
    ctx.globalAlpha = 1;
  }, [apercu]);

  // 🔴 Le chargement ne dépend PAS de `redessiner` (qui change avec l'aperçu) : il
  // recréait le masque à chaque bascule Peinture / Devant / Derrière — la peinture
  // disparaissait au moment même où l'on voulait la vérifier.
  const redessinerRef = useRef(redessiner);
  redessinerRef.current = redessiner;

  // Chargement du dessin : la vue est à l'échelle, le masque à la taille réelle.
  useEffect(() => {
    if (!ouvert || (!fichier && !urlExistante)) {
      imageRef.current = null;
      masqueRef.current = null;
      setAPeint(false);
      return undefined;
    }
    const local = !!fichier;
    const url = local ? URL.createObjectURL(fichier) : `${API_URL}${urlExistante}`;
    const image = new Image();
    // Servie par le backend (CORS ouvert à l'admin) : lisible, donc renvoyable telle quelle.
    if (!local) image.crossOrigin = 'anonymous';
    image.onload = () => {
      imageRef.current = image;
      const masque = document.createElement('canvas');
      masque.width = image.naturalWidth;
      masque.height = image.naturalHeight;
      masqueRef.current = masque;
      const vue = vueRef.current;
      if (vue) {
        const echelle = Math.min(1, 560 / image.naturalWidth, 380 / image.naturalHeight);
        vue.width = Math.round(image.naturalWidth * echelle);
        vue.height = Math.round(image.naturalHeight * echelle);
      }
      setAPeint(false);
      redessinerRef.current();
      // Objet existant découpé : sa moitié arrière devient la peinture de départ.
      if (!local && arriereExistant) {
        const arriere = new Image();
        arriere.crossOrigin = 'anonymous';
        arriere.onload = () => {
          const ctx = masque.getContext('2d');
          ctx.drawImage(arriere, 0, 0, masque.width, masque.height);
          ctx.globalCompositeOperation = 'source-in';
          ctx.fillStyle = '#e53935';
          ctx.fillRect(0, 0, masque.width, masque.height);
          ctx.globalCompositeOperation = 'source-over';
          setAPeint(true);
          redessinerRef.current();
        };
        arriere.src = `${API_URL}${arriereExistant}`;
      }
    };
    image.onerror = () => setErreur(local ? 'Dessin illisible' : `Dessin introuvable : ${urlExistante}`);
    image.src = url;
    return () => { if (local) URL.revokeObjectURL(url); };
  }, [ouvert, fichier, urlExistante, arriereExistant, vueEl]);

  useEffect(() => { redessiner(); }, [apercu, redessiner]);

  const peindre = (ev) => {
    const vue = vueRef.current;
    const masque = masqueRef.current;
    if (!vue || !masque || apercu !== 'peinture') return;
    const vueBoite = vue.getBoundingClientRect();
    // Coordonnées du masque : il est à la taille RÉELLE du dessin, la vue est réduite.
    const x = ((ev.clientX - vueBoite.left) / vueBoite.width) * masque.width;
    const y = ((ev.clientY - vueBoite.top) / vueBoite.height) * masque.height;
    const rayon = (pinceau / 2) * (masque.width / vue.width);
    const ctx = masque.getContext('2d');
    ctx.globalCompositeOperation = outil === 'gommer' ? 'destination-out' : 'source-over';
    ctx.fillStyle = '#e53935';
    ctx.beginPath();
    ctx.arc(x, y, rayon, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
    setAPeint(true);
    redessiner();
  };

  const effacerMasque = () => {
    const masque = masqueRef.current;
    if (!masque) return;
    masque.getContext('2d').clearRect(0, 0, masque.width, masque.height);
    setAPeint(false);
    redessiner();
  };

  const fermer = () => {
    setForm(formulaireVide());
    setFichier(null);
    setErreur(null);
    setAPeint(false);
    onFermer?.();
  };

  const masqueEnBlob = () => new Promise((resolve) => {
    if (!aPeint || !masqueRef.current) return resolve(null);
    return masqueRef.current.toBlob(resolve, 'image/png');
  });

  /** Objet existant : le dessin (nouveau fichier, ou celui en place) + la peinture + le cadre. */
  const enregistrer = async () => {
    setErreur(null);
    setEnCours(true);
    try {
      const dessin = fichier || await fetch(`${API_URL}${urlExistante}`).then((r) => {
        if (!r.ok) throw new Error(`Dessin introuvable : ${urlExistante}`);
        return r.blob();
      });
      const donnees = new FormData();
      donnees.append('dessin', dessin, fichier?.name || 'dessin.webp');
      if (cadre !== null) donnees.append('cadre', String(cadre));
      const masque = await masqueEnBlob();
      if (masque) donnees.append('masque', masque, 'masque.png');
      const resultat = await AdminService.uploadCatalogItemDrawing(objet.id, donnees);
      onEnregistre?.(resultat?.item);
      fermer();
    } catch (e) {
      setErreur(e?.data?.error || e?.message || 'Enregistrement impossible');
    } finally {
      setEnCours(false);
    }
  };

  const creer = async () => {
    setErreur(null);
    if (!form.name.trim()) return setErreur('Le nom est obligatoire');
    if (!/^[a-z0-9_]{3,50}$/.test(form.id)) return setErreur('Identifiant : 3 à 50 caractères, minuscules, chiffres et « _ »');
    if (!fichier) return setErreur('Le dessin est obligatoire');
    setEnCours(true);
    try {
      const sousType = TYPES.find((t) => t.type === form.type)?.subType || null;
      const cree = await AdminService.createCatalogItem({
        id: form.id,
        name: form.name.trim(),
        description: form.description || null,
        icon: form.icon || null,
        type: form.type,
        subType: sousType,
        rarity: form.rarity,
        price: Number(form.price),
        levelRequired: Number(form.levelRequired),
      });
      const donnees = new FormData();
      donnees.append('dessin', fichier);
      if (cadre !== null) donnees.append('cadre', String(cadre));
      const masque = await masqueEnBlob();
      if (masque) donnees.append('masque', masque, 'masque.png');
      const avecDessin = await AdminService.uploadCatalogItemDrawing(form.id, donnees);
      onCree?.(avecDessin?.item || cree?.item, cree?.placement);
      fermer();
    } catch (e) {
      setErreur(e?.data?.error || e?.message || 'Création impossible');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={!!ouvert} onClose={enCours ? undefined : fermer} maxWidth="md" fullWidth>
      <DialogTitle>{objet ? <>Dessin · {objet.name} <code style={{ fontSize: 13 }}>{objet.id}</code></> : 'Nouvel objet porté'}</DialogTitle>
      <DialogContent dividers>
        {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}
        <Stack spacing={2}>
          {objet && (
            <Stack direction="row" spacing={2} alignItems="center">
              {objet.inventoryImageUrl && (
                <img src={`${API_URL}${objet.inventoryImageUrl}`} alt="" width={72} height={72} />
              )}
              <Typography variant="body2" color="text.secondary">
                Icône actuelle. Le dessin en place est chargé ci-dessous
                {arriereExistant ? ', avec sa découpe (en rouge : ce qui passe derrière le compagnon)' : ''}.
                Enregistrer refait l'icône avec le cadre choisi et redécoupe les deux moitiés. La
                place de l'objet sur les compagnons ne change pas.
              </Typography>
            </Stack>
          )}
          {!objet && (<>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Nom" value={form.name} onChange={(e) => champ('name', e.target.value)}
              fullWidth autoFocus
            />
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel>Emplacement</InputLabel>
              <Select label="Emplacement" value={form.type} onChange={(e) => champ('type', e.target.value)}>
                {TYPES.map((t) => <MenuItem key={t.type} value={t.type}>{t.libelle}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>

          <TextField
            label="Identifiant" value={form.id}
            onChange={(e) => setForm((f) => ({ ...f, id: e.target.value, idTouche: true }))}
            helperText={base
              ? `Variante : cet objet se posera tout seul là où est posé « ${base} », sur tous les compagnons — rien à placer.`
              : 'Un suffixe « _2 », « _3 »… en fait une variante : elle reprend la boîte de l’objet de base, sans placement.'}
            fullWidth
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Prix (pièces)" type="number" value={form.price}
              onChange={(e) => champ('price', e.target.value)} sx={{ width: 160 }}
            />
            <TextField
              label="Niveau requis" type="number" value={form.levelRequired}
              onChange={(e) => champ('levelRequired', e.target.value)} sx={{ width: 160 }}
            />
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Rareté</InputLabel>
              <Select label="Rareté" value={form.rarity} onChange={(e) => champ('rarity', e.target.value)}>
                {RARETES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Emoji" value={form.icon} onChange={(e) => champ('icon', e.target.value)}
              sx={{ width: 110 }}
            />
          </Stack>

          <TextField
            label="Description" value={form.description}
            onChange={(e) => champ('description', e.target.value)} fullWidth multiline rows={2}
          />

          </>)}
          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>Dessin</Typography>
            <Typography variant="caption" color="text.secondary">
              Un seul fichier (png ou webp, fond transparent) : le dessin ENTIER. L'icône de boutique
              en est fabriquée. Peindre ensuite ce qui doit passer DERRIÈRE le compagnon — une écharpe,
              une cape, les branches de lunettes — le reste passera devant.
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Button variant="outlined" component="label">
                {fichier ? 'Changer le dessin' : (objet ? 'Remplacer par un autre fichier' : 'Choisir le dessin')}
                <input
                  hidden type="file" accept="image/png,image/webp"
                  onChange={(e) => { setFichier(e.target.files?.[0] || null); setApercu('peinture'); }}
                />
              </Button>
              {fichier && <Typography variant="caption" sx={{ ml: 1 }}>{fichier.name}</Typography>}
            </Box>
          </Box>

          {aUnDessin && (
            <Box>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                <ToggleButtonGroup
                  size="small" exclusive value={outil}
                  onChange={(_e, v) => v && setOutil(v)}
                >
                  <ToggleButton value="peindre">Peindre l'arrière</ToggleButton>
                  <ToggleButton value="gommer">Gommer</ToggleButton>
                </ToggleButtonGroup>
                <Box sx={{ width: 160 }}>
                  <Typography variant="caption">Pinceau</Typography>
                  <Slider size="small" min={4} max={120} value={pinceau} onChange={(_e, v) => setPinceau(v)} />
                </Box>
                <Button size="small" onClick={effacerMasque} disabled={!aPeint}>Tout effacer</Button>
                <ToggleButtonGroup
                  size="small" exclusive value={apercu}
                  onChange={(_e, v) => v && setApercu(v)}
                >
                  <ToggleButton value="peinture">Peinture</ToggleButton>
                  <ToggleButton value="avant" disabled={!aPeint}>Devant</ToggleButton>
                  <ToggleButton value="arriere" disabled={!aPeint}>Derrière</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              <Box
                sx={{
                  display: 'inline-block',
                  border: '1px solid', borderColor: 'divider', borderRadius: 1,
                  // Damier : on voit la transparence, donc la découpe.
                  backgroundImage: 'linear-gradient(45deg,#eee 25%,transparent 25%),linear-gradient(-45deg,#eee 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#eee 75%),linear-gradient(-45deg,transparent 75%,#eee 75%)',
                  backgroundSize: '16px 16px',
                  backgroundPosition: '0 0,0 8px,8px -8px,-8px 0',
                }}
              >
                <canvas
                  ref={(el) => { vueRef.current = el; setVueEl(el); }}
                  style={{ display: 'block', cursor: apercu === 'peinture' ? 'crosshair' : 'default', touchAction: 'none' }}
                  onPointerDown={(e) => { dessineRef.current = true; e.currentTarget.setPointerCapture(e.pointerId); peindre(e); }}
                  onPointerMove={(e) => { if (dessineRef.current) peindre(e); }}
                  onPointerUp={() => { dessineRef.current = false; }}
                  onPointerLeave={() => { dessineRef.current = false; }}
                />
              </Box>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                {aPeint
                  ? 'Deux moitiés seront découpées dans le même cadre : ce qui est peint passe derrière le compagnon.'
                  : 'Rien de peint : l’objet sera dessiné d’un seul plan, devant le compagnon.'}
              </Typography>
            </Box>
          )}

          {aUnDessin && cadres.length > 0 && (
            <Box>
              <Typography variant="subtitle2">Cadre de l'icône d'inventaire</Typography>
              <Typography variant="caption" color="text.secondary">
                Le dessin y est posé détouré et <b>déborde</b> sur la bordure, comme les icônes
                déjà au catalogue. Le cuir est le cadre des objets du compagnon ; la pierre à runes
                est celui des décors du village.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                {cadres.map((c) => (
                  <Box
                    key={c.n}
                    onClick={() => setCadre(c.n)}
                    title={c.nom}
                    sx={{
                      cursor: 'pointer', p: 0.5, borderRadius: 1, textAlign: 'center',
                      border: '2px solid',
                      borderColor: cadre === c.n ? 'primary.main' : 'transparent',
                    }}
                  >
                    <img src={`${API_URL}${c.url}`} alt={c.nom} width={56} height={56} />
                    <Typography variant="caption" display="block">{c.nom}</Typography>
                  </Box>
                ))}
                <Box
                  onClick={() => setCadre(0)}
                  sx={{
                    cursor: 'pointer', p: 0.5, borderRadius: 1, textAlign: 'center', width: 64,
                    display: 'flex', flexDirection: 'column', justifyContent: 'center',
                    border: '2px solid', borderColor: cadre === 0 ? 'primary.main' : 'divider',
                  }}
                >
                  <Typography variant="caption">sans cadre</Typography>
                </Box>
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={fermer} disabled={enCours}>Annuler</Button>
        {objet ? (
          <Button variant="contained" onClick={enregistrer} disabled={enCours}>
            {enCours ? 'Enregistrement…' : 'Enregistrer le dessin'}
          </Button>
        ) : (
          <Button variant="contained" onClick={creer} disabled={enCours}>
            {enCours ? 'Création…' : 'Créer l’objet'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
